import React, { useEffect, useState } from "react";
import { formatMessageTime } from "../../../../services/coral/coral-utils";
import "./notifications-panel.scss";

const NotificationsPanel = ({ notifications = [], onNotificationClick, loading }) => {
  const [filter, setFilter] = useState("all");

  const [, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    const intervalId = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(intervalId);
  }, []);

  // Utility functions
  const getNotificationIcon = (type) => {
    const iconMap = {
      mention: "fas fa-at",
      alert: "fas fa-exclamation-triangle",
      default: "fas fa-bell"
    };
    return iconMap[type] || iconMap.default;
  };

  const getNotificationColor = (type) => {
    const colorMap = {
      mention: "primary",
      alert: "warning",
      default: "info"
    };
    return colorMap[type] || colorMap.default;
  };

  const getFilteredNotifications = () => {
    return notifications.filter((notification) => {
      if (filter === "all") return true;
      return notification.type === filter;
    });
  };

  const getNotificationTypes = () => [
    { value: "all", label: "All Alerts", count: notifications.length },
    {
      value: "mention",
      label: "Mentions",
      count: notifications.filter((n) => n.type === "mention").length,
    },
    {
      value: "alert",
      label: "Alerts",
      count: notifications.filter((n) => n.type === "alert").length,
    },
  ];

  const truncateMessage = (message, maxLength = 100) => {
    if (!message || message.length <= maxLength) return message;
    return `${message.substring(0, maxLength)}...`;
  };

  // Event handlers
  const clearAllNotifications = () => {
    console.log("Clearing all notifications");
  };

  const handleNotificationClick = (notification, e = null) => {
    if (e) e.stopPropagation();
    onNotificationClick && onNotificationClick(notification);
  };

  // Render functions
  const renderHeader = () => (
    <div className="notifications-header">
      <div className="header-content">
        <h2>
          <i className="fas fa-bell"></i>
          Notifications
        </h2>
        {/* <div className="header-actions">
          <button className="bouncy-button small red" onClick={clearAllNotifications}>
            <i className="fas fa-trash"></i>
            <span>Clear All</span>
          </button>
        </div> */}
      </div>
    </div>
  );

  const renderFilterButton = (type) => (
    <button
      key={type.value}
      className={`filter-btn ${filter === type.value ? "active" : ""}`}
      onClick={() => setFilter(type.value)}
    >
      {type.label}
      {type.count > 0 && <span className="count-badge">{type.count}</span>}
    </button>
  );

  const renderFilters = () => (
    <div className="notifications-filters">
      {getNotificationTypes().map(renderFilterButton)}
    </div>
  );

  const renderLoadingState = () => (
    <div className="no-notifications">
      <div className="no-notifications-content">
        <i className="fas fa-spinner fa-spin"></i>
        <h3>Loading alerts...</h3>
      </div>
    </div>
  );

  const renderEmptyState = () => {
    const filteredNotifications = getFilteredNotifications();
    const emptyMessage = filter === "all"
      ? "No alerts found. Alerts appear when someone mentions the business account."
      : `No ${getNotificationTypes().find((t) => t.value === filter)?.label.toLowerCase()} found.`;

    return (
      <div className="no-notifications">
        <div className="no-notifications-content">
          <i className="fas fa-bell-slash"></i>
          <h3>No alerts</h3>
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  };

  const renderNotificationActions = (notification) => (
    <div className="notification-actions">
      <button
        className="icon-button"
        title="Send Alert Email"
        onClick={(e) => handleNotificationClick(notification, e)}
      >
        <i className="fas fa-envelope"></i>
      </button>
      <button className="icon-button" title="View in Group">
        <i className="fas fa-eye"></i>
      </button>
    </div>
  );

  const renderNotificationContent = (notification) => (
    <div className="notification-content">
      <div className="notification-message">
        <strong>{notification.groupName || 'Group'}</strong>
        {notification.senderName && <span> - {notification.senderName}</span>}
      </div>
      <div className="notification-summary">
        {notification.message}
      </div>
      {notification.originalMessage && (
        <div className="notification-original">
          "{truncateMessage(notification.originalMessage)}"
        </div>
      )}
      <div className="notification-time mt-2 text-end">
        {formatMessageTime(notification.rawTimestamp || notification.timestamp)}
      </div>
    </div>
  );

  const renderNotificationItem = (notification) => (
    <div
      key={notification.id}
      className={`notification-item  ${getNotificationColor(notification.type)}`}
      onClick={() => handleNotificationClick(notification)}
    >
      <div className="notification-icon">
        <i className={getNotificationIcon(notification.type)}></i>
      </div>
      {renderNotificationContent(notification)}
      {renderNotificationActions(notification)}
    </div>
  );

  const renderNotificationsList = () => {
    const filteredNotifications = getFilteredNotifications();
    
    return (
      <div className="notifications-list">
        {filteredNotifications.map(renderNotificationItem)}
      </div>
    );
  };

  const renderFooter = () => {
    const filteredNotifications = getFilteredNotifications();
    
    if (filteredNotifications.length === 0) return null;

    return (
      <div className="notifications-footer">
        <div className="notifications-summary">
          Showing {filteredNotifications.length} of {notifications.length} notifications
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) return renderLoadingState();
    
    const filteredNotifications = getFilteredNotifications();
    if (filteredNotifications.length === 0) return renderEmptyState();
    
    return renderNotificationsList();
  };

  return (
    <div className="notifications-panel">
      {renderHeader()}
      {renderFilters()}
      <div className="notifications-content">
        {renderContent()}
      </div>
      {renderFooter()}
    </div>
  );
};

export default NotificationsPanel;
